<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcAccountSelector',
    inheritAttrs: false,
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save', 'input']);

  const props = defineProps({
    value: {
      type: null,
      required: false,
    },
    displayOwner: {
      type: Boolean,
      required: false,
    },
    cacheKey: {
      type: String,
      required: false,
    },
    accountType: {
      type: String,
      required: false,
    },
    readonly: {
      type: Boolean,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const createAccountGrups = (...args) => {
    const [accounts] = args;
    const showInactive = states.value.showInactive;
    const accountType = props.accountType;

    return _.chain(accounts)
      .filter((p) => (accountType ? p.account_type === accountType : true))
      .groupBy((p) => p.owner.id)
      .map((items, owner_id) => ({
        id: owner_id,
        name: items[0].owner.code + ' - ' + items[0].owner.name,
        items: !showInactive ? _.filter(items, { inactive: false }) : items,
      }))
      .filter((p) => p.items.length)
      .value();
  };
  const itemChanged = (...args) => {
    const [item, value] = args;

    if (value == null || item == null) {
      return true;
    }
    return value.id != item.id;
  };

  const accounts = ref(null);
  const hasInactiveItem = ref(null);
  const accountGroups = ref(null);
  const states = ref({
    selectedAccountId: null,
    showInactive: false,
  });
  const ux = ref([]);

  const $data = computed(() => ({
    accounts: accounts.value,
    hasInactiveItem: hasInactiveItem.value,
    accountGroups: accountGroups.value,
    states: states.value,
    ux: ux.value,
  }));

  const $$load_accounts = async () => {
    return $fn.value
      .fetch('list_account')
      .then((resp) =>
        _.orderBy(
          resp.data,
          ['inactive', 'account_type', 'name'],
          ['asc', 'desc', 'asc']
        )
      );
  };
  const $$load_hasInactiveItem = async () => {
    return _.some($data.value.accounts, { inactive: true });
  };
  const $$load_accountGroups = async () => {
    return createAccountGrups($data.value.accounts);
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'accounts' },
      { name: 'hasInactiveItem', dependsOn: ['accounts'] },
      { name: 'accountGroups', dependsOn: ['accounts'] },
      {
        name: 'states',
        cache: {
          type: 'local',
          key: "{{ cacheKey ? ('AccountSelector.' + cacheKey) : null }}",
        },
      },
      { name: 'ux' },
    ],
    loaders: {
      accounts: $$load_accounts,
      hasInactiveItem: $$load_hasInactiveItem,
      accountGroups: $$load_accountGroups,
    },
    ux: {},
    dataRefs: { accounts, hasInactiveItem, accountGroups, states, ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const selectedAccountType = computed(() => {
    return props.value && props.value.account_type;
  });
  const selectedAccount = computed(() => {
    return _.isObject(props.value) ? props.value : null;
  });

  watch(
    () => props.value,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('value');
    }
  );
  watch(
    () => props.displayOwner,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('displayOwner');
    }
  );
  watch(
    () => props.cacheKey,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('cacheKey');
    }
  );
  watch(
    () => props.accountType,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('accountType');
    }
  );
  watch(
    () => props.readonly,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('readonly');
    }
  );

  const act_1468474219929533_setData_xr46knh8v_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'accountGroups',
      value: createAccountGrups(accounts.value),
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_1306227577716690_emit_zd4ndgqye_getActionArgs = async ($event) => {
    return {
      event: 'input',
      value: $event.scope.item,
    };
  };
  const inst_evh_1306227577716690_clickPrevent_1u9r5h64g_getWhen = async (
    $event
  ) => {
    return itemChanged($event.scope.item, props.value);
  };
  const act_1306227577716690_setData_7jm75bqre_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'states.selectedAccountId',
      value: $event.scope.item.id,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };

  const act_1468474219929533_setData_xr46knh8v = new actionTypes.SetDataAction({
    actionArgs: act_1468474219929533_setData_xr46knh8v_getActionArgs,
  });
  const inst_evh_1468474219929533_change_l2yi9j15e =
    new actionTypes.EventHandlerImpl({
      action: act_1468474219929533_setData_xr46knh8v,
      event: 'change',
      displayName: 'setData',
    });
  const inst_evh_1468474219929533_change = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1468474219929533_change_l2yi9j15e],
    event: 'change',
  });
  const act_1306227577716690_emit_zd4ndgqye = new actionTypes.EmitAction({
    actionArgs: act_1306227577716690_emit_zd4ndgqye_getActionArgs,
  });
  const inst_evh_1306227577716690_clickPrevent_1u9r5h64g =
    new actionTypes.EventHandlerImpl({
      action: act_1306227577716690_emit_zd4ndgqye,
      event: 'click.prevent',
      when: inst_evh_1306227577716690_clickPrevent_1u9r5h64g_getWhen,
      displayName: 'emit',
    });
  const act_1306227577716690_setData_7jm75bqre = new actionTypes.SetDataAction({
    actionArgs: act_1306227577716690_setData_7jm75bqre_getActionArgs,
  });
  const inst_evh_1306227577716690_clickPrevent_clcf05vi4 =
    new actionTypes.EventHandlerImpl({
      action: act_1306227577716690_setData_7jm75bqre,
      event: 'click.prevent',
      displayName: 'setData',
    });
  const inst_evh_1306227577716690_clickPrevent =
    new actionTypes.EventHandlerGroup({
      handlers: [
        inst_evh_1306227577716690_clickPrevent_1u9r5h64g,
        inst_evh_1306227577716690_clickPrevent_clcf05vi4,
      ],
      event: 'click.prevent',
    });

  const actions = {
    inst_evh_1468474219929533_change_l2yi9j15e,
    inst_evh_1468474219929533_change,
    inst_evh_1306227577716690_clickPrevent_1u9r5h64g,
    inst_evh_1306227577716690_clickPrevent_clcf05vi4,
    inst_evh_1306227577716690_clickPrevent,
  };

  const evh_1468474219929533_change_l2yi9j15e = async (event, scope) => {
    return await actions.inst_evh_1468474219929533_change_l2yi9j15e.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1468474219929533_change = async (event, scope) => {
    return await actions.inst_evh_1468474219929533_change.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1306227577716690_clickPrevent_1u9r5h64g = async (event, scope) => {
    return await actions.inst_evh_1306227577716690_clickPrevent_1u9r5h64g.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1306227577716690_clickPrevent_clcf05vi4 = async (event, scope) => {
    return await actions.inst_evh_1306227577716690_clickPrevent_clcf05vi4.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1306227577716690_clickPrevent = async (event, scope) => {
    return await actions.inst_evh_1306227577716690_clickPrevent.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <div>
    <div class="form-control form-control-readonly" v-if="readonly">
      <UcAccountSelectOption :payload="value" display-owner>
      </UcAccountSelectOption>
    </div>
    <BDropdown
      toggle-class="border"
      variant="light"
      class="apply-inline-button"
      v-if="!readonly"
    >
      <template #button-content>
        <ItpText
          :text="pgettext('AccountSelector', 'Select account')"
          v-if="!selectedAccount"
        >
        </ItpText>
        <UcAccountSelectOption
          :payload="value"
          display-owner
          v-if="selectedAccount"
        >
        </UcAccountSelectOption>
      </template>
      <BDropdownForm v-if="hasInactiveItem">
        <BFormCheckbox
          v-model="states.showInactive"
          @change="evh_1468474219929533_change($event, {})"
        >
          {{ pgettext('AccountSelector', 'Show inactive') }}
        </BFormCheckbox>
      </BDropdownForm>
      <BDropdownDivider v-if="hasInactiveItem"> </BDropdownDivider>
      <BDropdownGroup
        :id="group.id"
        :key="group.id"
        :header="group.name"
        v-for="group in accountGroups"
      >
        <BDropdownItemButton
          :key="item.id"
          v-for="item in group.items"
          @click.prevent="
            evh_1306227577716690_clickPrevent($event, { group, item })
          "
        >
          <UcAccountSelectOption :payload="item"> </UcAccountSelectOption>
        </BDropdownItemButton>
      </BDropdownGroup>
    </BDropdown>
  </div>
</template>
